import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { CourseDetail, CourseProgress, ModuleAssessment } from '../../types/content';
import Button from '../../components/UI/Button';
import CourseLearnAside from '../../components/content/CourseLearnAside';
import AuthorCard from '../../components/content/AuthorCard';
import CourseToc from '../../components/content/CourseToc';
import MobileHamburgerNav from '../../components/content/MobileHamburgerNav';
import TopicNavLinks from '../../components/content/TopicNavLinks';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import { moduleFlow } from '../../utils/moduleFlow';
import { courseTopicPath } from '../../utils/coursePaths';

const CourseAssessmentPage: React.FC = () => {
  const { slug, assessmentId } = useParams();
  const navigate = useNavigate();
  const id = Number(assessmentId);
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [assessment, setAssessment] = useState<ModuleAssessment | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interviewPackageCount, setInterviewPackageCount] = useState(0);
  const [mobileContentsOpen, setMobileContentsOpen] = useState(false);

  useEffect(() => {
    if (!slug || !id) return;
    let cancelled = false;
    contentApi.getCourseBySlug(slug).then((r) => {
      if (cancelled) return;
      const loaded = r.data.course;
      if (!loaded) {
        setError('Course not found.');
        return;
      }
      setCourse(loaded);
      contentApi.getCourseEnrollment(loaded.id)
        .then((e) => {
          const enrollment = e.data.enrollment as { interviewPackageCount?: number; InterviewPackageCount?: number } | null | undefined;
          setInterviewPackageCount(enrollment?.interviewPackageCount ?? enrollment?.InterviewPackageCount ?? 0);
        })
        .catch(() => setInterviewPackageCount(0));
      contentApi.getAssessment(loaded.id, id).then((a) => {
        if (cancelled) return;
        setAssessment(a.data.assessment || null);
        setProgress(a.data.progress || null);
        setAnswers({});
        if (!a.data.success) setError(a.data.message || 'Assessment not available.');
      }).catch(() => {
        if (!cancelled) setError('Assessment not available.');
      });
    }).catch(() => {
      if (!cancelled) setError('Course not found.');
    });
    return () => { cancelled = true; };
  }, [slug, id]);

  useEffect(() => {
    setMobileContentsOpen(false);
  }, [assessmentId]);

  const neighbors = useMemo(() => {
    if (!course) return { previousHref: null as string | null, previousTitle: null as string | null, nextHref: null as string | null, nextTitle: null as string | null };
    const flat = course.modules.flatMap((module) => moduleFlow(module.topics, module.assessments).map((item) => ({ module, item })));
    const index = flat.findIndex((row) => row.item.kind === 'assessment' && row.item.assessment.id === id);
    const prev = index > 0 ? flat[index - 1] : null;
    const next = index >= 0 && index + 1 < flat.length ? flat[index + 1] : null;
    const hrefFor = (row: typeof flat[number] | null) => {
      if (!row) return { href: null as string | null, title: null as string | null };
      if (row.item.kind === 'topic' && row.item.topic.slug) {
        return { href: courseTopicPath(course.slug, row.item.topic.slug, row.module.id), title: row.item.topic.title };
      }
      if (row.item.kind === 'assessment' && row.item.assessment.id) {
        return { href: `/courses/${course.slug}/assessments/${row.item.assessment.id}`, title: row.item.assessment.title };
      }
      return { href: null, title: null };
    };
    const previous = hrefFor(prev);
    const following = hrefFor(next);
    return { previousHref: previous.href, previousTitle: previous.title, nextHref: following.href, nextTitle: following.title };
  }, [course, id]);

  const tocModules = (progress?.modules?.length ? progress.modules : course?.modules || []).map((module) => ({
    id: module.id,
    title: module.title,
    isCompleted: 'isCompleted' in module ? module.isCompleted : false,
    topics: module.topics.map((item) => ({
      title: item.title,
      slug: item.slug,
      displayOrder: item.displayOrder,
      isCompleted: 'isCompleted' in item ? item.isCompleted : !!(item.id && progress?.completedTopicIds?.includes(item.id)),
      labs: ('labs' in item ? item.labs : 'relatedLabs' in item ? item.relatedLabs : [])?.map((lab) => ({
        id: lab.id,
        name: lab.name,
        slug: lab.slug,
        isCompleted: 'isCompleted' in lab ? !!lab.isCompleted : !!(progress?.completedLabIds?.includes(lab.id)),
      })),
    })),
    assessments: ('assessments' in module ? module.assessments : []).map((item) => ({
      id: item.id,
      title: item.title,
      displayOrder: item.displayOrder,
      passed: 'passed' in item ? item.passed : !!(item.id && progress?.passedAssessmentIds?.includes(item.id)),
    })),
  }));

  if (error && !course) return <div className="max-w-3xl mx-auto px-4 py-16 text-gray-600">{error}</div>;
  if (!course) return <PageLoading label="Loading assessment…" />;

  const submit = async () => {
    if (!assessment?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload = Object.entries(answers).map(([questionId, optionId]) => ({
        questionId: Number(questionId),
        optionId,
      }));
      const response = await contentApi.submitAssessment(course.id, assessment.id, payload);
      setAssessment(response.data.assessment || assessment);
      setProgress(response.data.progress || null);
      setMessage(response.data.message || (response.data.assessment?.passed ? 'Passed.' : 'Try again.'));
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : 'Submit failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <article>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[17rem_minmax(0,1fr)_17rem] gap-8 items-start">
        {tocModules.length > 0 && (
          <MobileHamburgerNav
            open={mobileContentsOpen}
            onToggle={() => setMobileContentsOpen((open) => !open)}
            label="table of contents"
          >
            <CourseToc
              courseSlug={course.slug}
              modules={tocModules}
              currentAssessmentId={assessment?.id}
              interviewPackageCount={interviewPackageCount}
              courseId={course.id}
              courseTitle={course.title}
              showInterviewLinks={false}
              onNavigate={() => setMobileContentsOpen(false)}
            />
          </MobileHamburgerNav>
        )}
        {tocModules.length > 0 && (
          <aside className="hidden lg:block lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            <CourseToc
              courseSlug={course.slug}
              modules={tocModules}
              currentAssessmentId={assessment?.id}
              interviewPackageCount={interviewPackageCount}
              courseId={course.id}
              courseTitle={course.title}
              showInterviewLinks={false}
            />
          </aside>
        )}
        <div className="min-w-0">
          {!assessment ? (
            <p className="text-gray-600">Loading assessment…</p>
          ) : (
            <>
      <nav className="text-sm text-gray-500">
        <Link to={`/courses/${course.slug}`} className="text-primary-600">{course.title}</Link>
        <span> / Assessment</span>
      </nav>
      <h1 className="mt-4 text-3xl font-bold text-gray-900">{assessment.title}</h1>
      {assessment.description && <p className="mt-2 text-gray-600">{assessment.description}</p>}
      <p className="mt-2 text-sm text-gray-500">Passing score: {assessment.passingScore}%</p>
      {(() => {
        const moduleAuthor = course.modules?.find((module) => (module.assessments || []).some((item) => item.id === assessment.id))?.author;
        return moduleAuthor?.displayName ? (
          <div className="mt-6 max-w-xl"><AuthorCard author={moduleAuthor} compact /></div>
        ) : null;
      })()}
      {assessment.passed ? (
        <p className="mt-4 text-success-700 font-medium">You passed{assessment.lastScore != null ? ` with ${assessment.lastScore}%` : ''}.</p>
      ) : null}
      {message && <p className="mt-4 text-sm text-primary-800 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">{message}</p>}
      <div className="mt-8 space-y-6">
        {assessment.questions.map((question, qi) => (
          <fieldset key={question.id || qi} className="rounded-2xl border border-gray-200 p-4">
            <legend className="font-medium text-gray-900">Q{qi + 1}. {question.prompt}</legend>
            <div className="mt-3 space-y-2">
              {question.options.map((option) => (
                <label key={option.id} className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name={`q-${question.id}`}
                    disabled={!!assessment.passed}
                    checked={answers[question.id!] === option.id}
                    onChange={() => setAnswers((current) => ({ ...current, [question.id!]: option.id! }))}
                  />
                  <span>
                    {option.text}
                    {option.isCorrect ? <span className="ml-2 text-xs text-success-700">Correct</span> : null}
                  </span>
                </label>
              ))}
            </div>
            {question.explanation && assessment.passed ? (
              <p className="mt-3 text-sm text-gray-600">{question.explanation}</p>
            ) : null}
          </fieldset>
        ))}
      </div>
      <div className="mt-8 flex gap-3">
        {!assessment.passed && (
          <Button type="button" onClick={submit} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit assessment'}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => navigate(`/courses/${course.slug}`)}>
          Back to course
        </Button>
      </div>
      <TopicNavLinks
        previousTitle={neighbors.previousTitle}
        previousHref={neighbors.previousHref}
        nextTitle={neighbors.nextTitle}
        nextHref={neighbors.nextHref}
        toTopic={(topicPath) => courseTopicPath(course.slug, topicPath)}
      />
      {progress?.isCourseCompleted ? (
        <p className="mt-6 text-success-700 font-medium">Course completed. All required topics, labs, and assessments are done.</p>
      ) : null}
            </>
          )}
        </div>
        <div className="hidden lg:block">
          <CourseLearnAside
            courseId={course.id}
            courseSlug={course.slug}
            courseTitle={course.title}
            interviewPackageCount={interviewPackageCount}
            averageRating={course.averageRating}
            ratingCount={course.ratingCount}
            onRated={(average, count) => setCourse((current) => current ? { ...current, averageRating: average, ratingCount: count } : current)}
          />
        </div>
      </div>
    </article>
  );
};

export default CourseAssessmentPage;
